import React, { forwardRef, useState, useEffect, useRef } from 'react'
import CreatableSelect from 'react-select/creatable'
import { Controller } from 'react-hook-form'
import { ComboSelectStyle } from '../../../utils/ComboSelectStyle'
import { HIGHER_PRICE_BOUND, LOWER_PRICE_BOUND, validate, stripHTMLFromString, clearTitleAndTagsForbiddenWords,
} from '../helpers'
import BundlesPriceModal from '../../../components/publisher/EditHeader/BundlesPriceModal'

const Combobox = forwardRef(({
  name,
  control,
  options,
  type,
  totalPrice,
  setBundlePriceValidation,
  errorMessage,
  setBundlePrice,
  register,
  product,
  bundlePrice,
  i18nOptions,
  ...props
}) => {
  const [inputValue, setInputValue] = useState('')
  const [showModal, setShowModal] = useState(false)
  const selectRef = useRef(null)

  const handleValidation = (value) => {
    if (Number(totalPrice) === 0) {
      setBundlePriceValidation(null)
    } else if (Number(totalPrice) > 0) {
      if (Number(value) === 0) {
        setBundlePriceValidation({ type: 'minLength' })
      } else if (Number(value) > Number(totalPrice)) {
        setBundlePriceValidation({ type: 'maxLengthBundle' })
      } else {
        setBundlePriceValidation(null)
      }
    }
  }

  const sanitizeValue = (value) => {
    const parsedValue = parseFloat(String(value))
    if (isNaN(parsedValue)) return ''
    handleValidation(parsedValue)
    const formattedValue = parsedValue.toFixed(2)
    setBundlePrice(formattedValue)
    return clearTitleAndTagsForbiddenWords(stripHTMLFromString(formattedValue)).trim()
  }

  const setDefaultValue = () => {
    if (product.price) {
      setInputValue(product.price)
      setBundlePrice(product.price)
    }
  }

  useEffect(() => {
    setDefaultValue()
  }, [])

  return (
    <div data-testid="price-input">
      <Controller
        name={name}
        id={name}
        control={control}
        rules={{
          required: {
            value: true,
            short: 'Required',
            message: errorMessage('price_required'),
          },
          setValueAs: sanitizeValue,
        }}
        render={({ field }) => (
          <CreatableSelect
            maxMenuHeight={299}
            {...props}
            name={name}
            data-testid="price-input"
            value={
              field.value
                ? { label: `$${field.value}`, value: field.value }
                : null
            }
            onChange={(selectedOption) => {
              const selectedValue = selectedOption ? selectedOption.value : ''
              const sanitized = sanitizeValue(selectedValue)
              field.onChange(sanitized)
              setInputValue(sanitized)
            }}
            onInputChange={(input, actionMeta) => {
              if (actionMeta.action === 'input-change') {
                const sanitizedInput = input
                  .replace(/-/g, '')
                  .replace(/\./g, ',')
                setInputValue(sanitizedInput)
              }
            }}
            onBlur={() => {
              field.onBlur()
              if (inputValue) {
                const sanitized = sanitizeValue(inputValue)
                field.onChange(sanitized)
                setInputValue(sanitized)
                if (Number(sanitized) > totalPrice) {
                  setShowModal(true)
                }
              } else {
                setBundlePrice('')
                setInputValue('')
                field.onChange('')
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (inputValue) {
                  const sanitized = sanitizeValue(inputValue)
                  field.onChange(sanitized)
                  setInputValue(sanitized)
                  if (Number(sanitized) > totalPrice) {
                    setShowModal(true)
                  }
                  if (selectRef.current) {
                    selectRef.current.blur()
                  }
                } else {
                  setBundlePrice('')
                  setInputValue('')
                  field.onChange('')
                }
              }
            }}
            options={options}
            inputValue={inputValue}
            filterOption={null}
            placeholder=""
            styles={ComboSelectStyle}
            ref={selectRef}
          />
        )}
      />
      <BundlesPriceModal
        i18nOptions={i18nOptions}
        recommendedPrice={totalPrice}
        show={showModal}
        //when user acknowledges the error by clicking the 'ok' button
        hideModalButton={() => {
          setShowModal(false)
          setInputValue('0')
          setBundlePrice('0.00')
        }}
        //when user clicks the close button or outside of the modal
        hideModal={() => {
          setShowModal(false)
        }}
      />
    </div>
  )
})

export default Combobox
